import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { COMPARE_IDS } from "../../../Components/Filters/CompareSelect/presets"
import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { HEALTH_SLICE_NAME } from "./health.constants"
import {
  createVampEnrollment,
  getDataByCardType,
  getDataByCountry,
  getDataByReasonCode,
  getDataOlympics,
  getEfmMonitoringProgramChart,
  getFilterOptions,
  getHealthMainInfo,
  getHealthOverview,
  getMcEstimatedProgramStatus,
  getMcMainChart,
  getMcMonitoringProgramChart,
  getPreventionRate,
  getRiskLevel,
  getVampDescriptors,
  getVampEstimatedProgramStatus,
  getVampMainChart,
  getVampMonitoringProgramChart,
  monitoringInit
} from "./health.thunks"

/* ------------- Initial State ------------- */

const DEFAULT_HEALTH_FILTERS = {
  period: "27 days",
  overviewType: "rate", // 'count' | 'rate'
  compare: { mode: COMPARE_IDS.PREV_PERIOD },
  lastCompare: null
}

const initialState = {
  fetching: false,
  mainInfo: {
    is_business_health_available: false,
    warning_notification: null, // "missing_stripe_connection" | "missing_stripe_permissions" |"report_in_progress"
    prevention_rate: 0,
    chargeback_rate: 0,
    chargeback_count: 0,
    chargeback_rate_without_us: 0,
    chargeback_count_without_us: 0,
    data_blocks: [
      {
        title: "Chargebacks prevented",
        tooltip: null,
        value: 0
      },
      {
        title: "Chargeback rate",
        tooltip: null,
        value: 0,
        suffix: "%",
        precision: 2
      },
      {
        title: "Prevention rate",
        tooltip: null,
        value: 0,
        suffix: "%",
        precision: 2
      },
      {
        title: "Chargeback fees saved",
        tooltip: null,
        value: 0,
        prefix: "$",
        precision: 2
      }
    ],
    filter_options: {
      years: []
    }
  },
  result: null,
  errors: false,

  preventionRateFetching: false,
  preventionRateData: [
    { month: "JAN", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "FEB", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "MAR", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "APR", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "MAY", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "JUN", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "JUL", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "AUG", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "SEP", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "OCT", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "NOV", preventionRate: 0, disputes_count: 0, alerts_count: 0 },
    { month: "DEC", preventionRate: 0, disputes_count: 0, alerts_count: 0 }
  ],
  preventionRateYear: 2025,

  dataByCardTypeFetching: false,
  dataByCardType: {
    result: [],
    summary: {
      transactions_count: 0,
      transactions_amount: 0,
      chargebacks_count: 0,
      chargebacks_amount: 0,
      alerts_count: 0,
      alerts_amount: 0,
      chargeback_rate: 0,
      chargeback_rate_without_us: 0,
      prevention_rate: 0
    }
  },

  selectCountries: [],
  dataByCountryFetching: false,
  dataByCountry: {
    count: 0,
    result: [],
    summary: {
      transactions_count: 0,
      transactions_amount: 0,
      chargebacks_count: 0,
      chargebacks_amount: 0,
      alerts_count: 0,
      alerts_amount: 0,
      chargeback_rate: 0,
      chargeback_rate_without_us: 0,
      prevention_rate: 0
    }
  },

  selectCards: [],
  dataByReasonCodeFetching: false,
  dataByReasonCode: {
    count: 0,
    result: [],
    summary: {
      type: null,
      chargebacks_count: 0,
      chargebacks_amount: 0,
      chargebacks_without_us_count: 0,
      chargebacks_without_us_amount: 0,
      alerts_count: 0,
      alerts_amount: 0,
      prevention_rate: 0
    }
  },

  dataOlympicsFetching: false,
  dataOlympics: {
    count: 0,
    results: []
  },

  overviewFetching: false,
  overview: {
    labels: [],
    result: {
      with_us: [],
      without_us: []
    }
  },

  riskLevelFetching: false,
  riskLevel: 0,

  filtersLastUpd: null,
  filters: { ...DEFAULT_HEALTH_FILTERS },
  // CHA1-4421 (item 2): each source tab keeps its own filters/period; `filters` mirrors the
  // active tab, swapped on source change in `healthSetProp`.
  filtersBySource: {
    stripe: { ...DEFAULT_HEALTH_FILTERS },
    shopify: { ...DEFAULT_HEALTH_FILTERS }
  },

  monitoringCompare: {
    compare: null,
    lastCompare: null
  },

  filterOptions: {
    years: [],
    card_brand: []
  },

  vampMainChart: null,
  vampMainChartFetching: false,

  vampMonitoringProgramChart: null,
  vampMonitoringProgramChartFetching: false,

  mcMainChart: null,
  mcMainChartFetching: false,

  mcMonitoringProgramChart: null,
  mcMonitoringProgramChartFetching: false,

  efmMonitoringProgramChart: null,
  efmMonitoringProgramChartFetching: false,

  mcEstimatedProgramStatus: null,
  mcEstimatedProgramStatusFetching: false,

  vampEstimatedProgramStatus: null,
  vampEstimatedProgramStatusFetching: false,

  monitoringInitialized: false,
  monitoringInitializing: true,
  monitoringInitError: null,

  descriptorsList: [],
  vampDescriptors: [],
  descriptor: null,
  // Business account the loaded descriptors belong to. Guards against firing
  // requests with a descriptor of the previously selected business account.
  descriptorsBusinessAccount: null,
  showEnrollmentModal: false,
  isHistoricalDataMode: false,
  enrollmentData: null,

  vampEnrollmentRequestLoading: false,

  dataSource: "stripe" // 'stripe' | 'shopify'
}

// The descriptors endpoint answers with positional rows - kept as a local helper because
// both `getVampDescriptors` and `monitoringInit` reshape their payload through it.
const toVampDescriptors = (rows) =>
  (rows || [])
    .filter((row) => row?.[0])
    .map(([name, ratio, transactions, cases]) => ({
      name,
      ratio: Number(ratio) || 0,
      transactions: Number(transactions) || 0,
      cases: Number(cases) || 0
    }))

const slice = createSlice({
  name: HEALTH_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments - `healthSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `healthSetProp({ key, value })`,
    // matching the `alertsSetProp`/`publicApiSetProp` slices.
    healthSetProp(state, action) {
      const { key, value } = action.payload

      if (key === "filters") {
        state.filtersLastUpd = new Date().getTime()
        state.filters = { ...state.filters, ...value }
      } else if (key === "dataSource" && value !== state.dataSource) {
        // CHA1-4421 (item 2): stash the current tab's filters and restore the target tab's own,
        // so a period set on one source doesn't leak to the other. (item 1) clear the per-source
        // country/card picks on switch - they may not exist for the new source.
        state.filtersBySource = {
          ...state.filtersBySource,
          [state.dataSource]: state.filters
        }
        state.dataSource = value
        state.filters = state.filtersBySource[value] || { ...DEFAULT_HEALTH_FILTERS }
        state.filtersLastUpd = new Date().getTime()
        state.selectCountries = []
        state.selectCards = []
      } else {
        state[key] = value
      }
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- overview dashboard ---------------- */
      .addCase(getHealthMainInfo.pending, (state) => {
        state.fetching = true
      })
      .addCase(getHealthMainInfo.fulfilled, (state, { payload }) => {
        state.fetching = false
        state.mainInfo = payload
      })
      .addCase(getHealthMainInfo.rejected, (state) => {
        state.fetching = false
        // CHA1-4421 (item 4): drop the previous mainInfo so a failed request on one source tab
        // doesn't leave the other source's banner/figures showing.
        state.mainInfo = initialState.mainInfo
      })

      .addCase(getHealthOverview.pending, (state) => {
        state.overviewFetching = true
      })
      .addCase(getHealthOverview.fulfilled, (state, { payload }) => {
        state.overviewFetching = false
        state.overview = payload
      })
      .addCase(getHealthOverview.rejected, (state) => {
        state.overviewFetching = false
      })

      .addCase(getPreventionRate.pending, (state) => {
        state.preventionRateFetching = true
      })
      .addCase(getPreventionRate.fulfilled, (state, { payload }) => {
        state.preventionRateFetching = false
        state.preventionRateData = payload
      })
      .addCase(getPreventionRate.rejected, (state) => {
        state.preventionRateFetching = false
      })

      .addCase(getDataByCardType.pending, (state) => {
        state.dataByCardTypeFetching = true
      })
      .addCase(getDataByCardType.fulfilled, (state, { payload }) => {
        state.dataByCardTypeFetching = false
        state.dataByCardType = payload
      })
      .addCase(getDataByCardType.rejected, (state) => {
        state.dataByCardTypeFetching = false
      })

      .addCase(getDataByCountry.pending, (state) => {
        state.dataByCountryFetching = true
      })
      .addCase(getDataByCountry.fulfilled, (state, { payload }) => {
        state.dataByCountryFetching = false
        state.dataByCountry = payload
      })
      .addCase(getDataByCountry.rejected, (state) => {
        state.dataByCountryFetching = false
      })

      .addCase(getDataByReasonCode.pending, (state) => {
        state.dataByReasonCodeFetching = true
      })
      .addCase(getDataByReasonCode.fulfilled, (state, { payload }) => {
        state.dataByReasonCodeFetching = false
        state.dataByReasonCode = payload
      })
      .addCase(getDataByReasonCode.rejected, (state) => {
        state.dataByReasonCodeFetching = false
      })

      .addCase(getDataOlympics.pending, (state) => {
        state.dataOlympicsFetching = true
      })
      .addCase(getDataOlympics.fulfilled, (state, { payload }) => {
        state.dataOlympicsFetching = false
        state.dataOlympics = payload
      })
      .addCase(getDataOlympics.rejected, (state) => {
        state.dataOlympicsFetching = false
      })

      .addCase(getRiskLevel.pending, (state) => {
        state.riskLevelFetching = true
      })
      .addCase(getRiskLevel.fulfilled, (state, { payload }) => {
        state.riskLevelFetching = false
        state.riskLevel = payload?.["risk_level"] || 0
      })
      .addCase(getRiskLevel.rejected, (state) => {
        state.riskLevelFetching = false
      })

      // `getFilterOptions` has no pending/rejected cases on purpose - the legacy reducer
      // returned state untouched for both, there is no spinner tied to it.
      .addCase(getFilterOptions.fulfilled, (state, { payload }) => {
        state.filterOptions = payload
      })

      /* ---------------- VAMP (Visa) ---------------- */
      .addCase(getVampMainChart.pending, (state) => {
        state.vampMainChartFetching = true
      })
      .addCase(getVampMainChart.fulfilled, (state, { payload }) => {
        state.vampMainChartFetching = false
        state.vampMainChart = payload
      })
      .addCase(getVampMainChart.rejected, (state) => {
        state.vampMainChartFetching = false
      })

      .addCase(getVampMonitoringProgramChart.pending, (state) => {
        state.vampMonitoringProgramChartFetching = true
      })
      .addCase(getVampMonitoringProgramChart.fulfilled, (state, { payload }) => {
        state.vampMonitoringProgramChartFetching = false
        state.vampMonitoringProgramChart = payload
      })
      .addCase(getVampMonitoringProgramChart.rejected, (state) => {
        state.vampMonitoringProgramChartFetching = false
      })

      .addCase(getVampEstimatedProgramStatus.pending, (state) => {
        state.vampEstimatedProgramStatusFetching = true
      })
      .addCase(getVampEstimatedProgramStatus.fulfilled, (state, { payload }) => {
        state.vampEstimatedProgramStatusFetching = false
        state.vampEstimatedProgramStatus = payload
      })
      .addCase(getVampEstimatedProgramStatus.rejected, (state) => {
        state.vampEstimatedProgramStatusFetching = false
      })

      // Same as `getFilterOptions`: the legacy request/failure reducers were no-ops.
      .addCase(getVampDescriptors.fulfilled, (state, { payload }) => {
        state.vampDescriptors = toVampDescriptors(payload)
      })

      /* ---------------- Mastercard (ECP & EFM) ---------------- */
      .addCase(getMcMainChart.pending, (state) => {
        state.mcMainChartFetching = true
      })
      .addCase(getMcMainChart.fulfilled, (state, { payload }) => {
        state.mcMainChartFetching = false
        state.mcMainChart = payload
      })
      .addCase(getMcMainChart.rejected, (state) => {
        state.mcMainChartFetching = false
      })

      .addCase(getMcMonitoringProgramChart.pending, (state) => {
        state.mcMonitoringProgramChartFetching = true
      })
      .addCase(getMcMonitoringProgramChart.fulfilled, (state, { payload }) => {
        state.mcMonitoringProgramChartFetching = false
        state.mcMonitoringProgramChart = payload
      })
      .addCase(getMcMonitoringProgramChart.rejected, (state) => {
        state.mcMonitoringProgramChartFetching = false
      })

      .addCase(getEfmMonitoringProgramChart.pending, (state) => {
        state.efmMonitoringProgramChartFetching = true
      })
      .addCase(getEfmMonitoringProgramChart.fulfilled, (state, { payload }) => {
        state.efmMonitoringProgramChartFetching = false
        state.efmMonitoringProgramChart = payload
      })
      .addCase(getEfmMonitoringProgramChart.rejected, (state) => {
        state.efmMonitoringProgramChartFetching = false
      })

      .addCase(getMcEstimatedProgramStatus.pending, (state) => {
        state.mcEstimatedProgramStatusFetching = true
      })
      .addCase(getMcEstimatedProgramStatus.fulfilled, (state, { payload }) => {
        state.mcEstimatedProgramStatusFetching = false
        state.mcEstimatedProgramStatus = payload
      })
      .addCase(getMcEstimatedProgramStatus.rejected, (state) => {
        state.mcEstimatedProgramStatusFetching = false
      })

      /* ---------------- monitoring bootstrap ---------------- */
      .addCase(monitoringInit.pending, (state) => {
        state.monitoringInitializing = true
        // CHA1-4491: clear the previous BA's monitoring data on (re)init so switching accounts
        // never shows the old BA's chart / estimated status / "last updated" while the new loads.
        state.mainInfo = initialState.mainInfo
        state.vampMainChart = null
        state.mcMainChart = null
        state.vampMonitoringProgramChart = null
        state.mcMonitoringProgramChart = null
        state.efmMonitoringProgramChart = null
        state.vampEstimatedProgramStatus = null
        state.mcEstimatedProgramStatus = null
      })
      .addCase(monitoringInit.fulfilled, (state, { payload }) => {
        const { descriptorsList, enrollmentData, business_account } = payload || {}
        const vampDescriptors = toVampDescriptors(descriptorsList)

        state.monitoringInitializing = false
        state.monitoringInitialized = true
        state.vampDescriptors = vampDescriptors
        state.descriptorsList = vampDescriptors.map((desc) => desc.name)
        state.descriptor = state.descriptorsList?.[0] || null
        state.descriptorsBusinessAccount = business_account || null
        state.enrollmentData = enrollmentData || null
        // Historical mode only survives while the account still offers historical data.
        if (!enrollmentData?.historical_data_available) state.isHistoricalDataMode = false
      })
      .addCase(monitoringInit.rejected, (state, { payload }) => {
        state.monitoringInitializing = false
        state.monitoringInitError = payload
      })

      .addCase(createVampEnrollment.pending, (state) => {
        state.vampEnrollmentRequestLoading = true
      })
      // The legacy success/failure reducers only cleared the flag - the fresh enrollment
      // data arrives through the `monitoringInit` the thunk chains on success.
      .addCase(createVampEnrollment.fulfilled, (state) => {
        state.vampEnrollmentRequestLoading = false
      })
      .addCase(createVampEnrollment.rejected, (state) => {
        state.vampEnrollmentRequestLoading = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)
  }
})

export const { healthSetProp } = slice.actions

// Continues the version chain of the legacy `health` persist key (it was at 10), so a store
// persisted by the saga reducer is discarded rather than merged into the new shape.
const PERSISTED_REDUCER_VERSION = 12

export const persistedReducer = persistReducer(
  {
    key: HEALTH_SLICE_NAME,
    storage,
    // Only the user's own view setup survives a reload - every chart and table on the page
    // is re-fetched on mount.
    whitelist: [
      "filters",
      "filtersBySource",
      "filterOptions",
      "filtersLastUpd",
      "dataSource",
      "preventionRateYear",
      "isHistoricalDataMode"
    ],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
